import { useAuth } from "@Auth/useAuth";
import {
  SuppliersClient,
  SupplierType,
} from "@Clients/SuppliersClient";
import { Footer } from "@common/SimpleDrawer/components/Footer";
import { Section } from "@common/SimpleDrawer/components/Section";
import { FormInput } from "@components/FormInput/FormInput";
import { FormAutocomplete } from "@components/FormAutocomplete/FormAutoComplete";
import { DRAWER_CONTEXT } from "@models/DrawerContext";
import { Entity } from "@models/Entity";
import { FORM_ACTIONS } from "@models/FormActions";
import { Box } from "@mui/material";
import { useConfirmationModalStore } from "@stores/confirmationModalStore";
import { useSuppliersStore } from "@stores/suppliersStore";
import { useDrawer } from "@hooks/useDrawer";
import { onErrorShow } from "@utils/onErrorShow";
import { onInfoShow } from "@utils/onInfoShow";
import { returnErrorMessage } from "@utils/returnErrorMessage";
import { useEffect, useReducer } from "react";

type SupplierInitialState = {
  name: string;
  email: string;
  phone: string;
  supplier_type: SupplierType;
  external_id: string;
  status: "active" | "inactive" | "suspended";
  address: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  contact_person: string;
  payment_terms: string;
  notes: string;
  errors: Record<keyof Omit<SupplierInitialState, "errors">, string>;
};

const initialState: SupplierInitialState = {
  name: "",
  email: "",
  phone: "",
  supplier_type: null,
  external_id: "",
  status: "active",
  address: "",
  city: "",
  state: "",
  postal_code: "",
  country: "",
  contact_person: "",
  payment_terms: "",
  notes: "",
  errors: {} as Record<keyof Omit<SupplierInitialState, "errors">, string>,
};

interface Action<T> {
  type: FORM_ACTIONS;
  field?: keyof T;
  value?: string | SupplierType;
  errors?: Record<keyof T, string>;
}

function formReducer(
  state: SupplierInitialState,
  action: Action<SupplierInitialState>
) {
  switch (action.type) {
    case FORM_ACTIONS.SET_FIELD: {
      if (!action.field) return state;
      return {
        ...state,
        [action.field!]: action.value,
        errors: {
          ...state.errors,
          [action.field!]: "",
        },
      };
    }
    case FORM_ACTIONS.SET_ERRORS:
      return {
        ...state,
        errors: action.errors || {},
      };
    case FORM_ACTIONS.RESET:
      return initialState;
    default:
      throw new Error(`Unknown action type: ${action.type}`);
  }
}

type SupplierFormProps = {
  handleClose: () => void;
  onSupplierCreated?: (supplierId: string) => void;
  createFromPurchaseOrderView?: boolean;
};

const supplierTypeOptions = [
  { label: "Manufacturer", value: "manufacturer" },
  { label: "Distributor", value: "distributor" },
  { label: "Wholesaler", value: "wholesaler" },
  { label: "Retailer", value: "retailer" },
  { label: "Other", value: "other" },
];

const statusOptions = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
  { label: "Suspended", value: "suspended" },
];

export const SupplierForm = ({
  handleClose,
  onSupplierCreated,
  createFromPurchaseOrderView,
}: SupplierFormProps) => {
  const {
    context,
    selectedId,
    close: closeOnSubmit,
  } = useDrawer();

  const { baseUser } = useAuth();
  const { getSuppliersByOrganizationId, suppliers } = useSuppliersStore();
  const { setTouch } = useConfirmationModalStore();

  const [state, dispatch] = useReducer(
    formReducer as React.Reducer<
      Partial<SupplierInitialState>,
      Action<SupplierInitialState>
    >,
    initialState
  );

  useEffect(() => {
    if (context === DRAWER_CONTEXT.EDIT_SUPPLIER && selectedId) {
      const supplier = suppliers.find((supplier) => supplier.id === selectedId);
      if (supplier) {
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "name",
          value: supplier.name,
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "email",
          value: supplier.email || "",
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "phone",
          value: supplier.phone || "",
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "supplier_type",
          value: supplier.supplier_type || null,
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "external_id",
          value: supplier.external_id || "",
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "status",
          value: supplier.status,
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "address",
          value: supplier.address || "",
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "city",
          value: supplier.city || "",
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "state",
          value: supplier.state || "",
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "postal_code",
          value: supplier.postal_code || "",
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "country",
          value: supplier.country || "",
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "contact_person",
          value: supplier.contact_person || "",
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "payment_terms",
          value: supplier.payment_terms || "",
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "notes",
          value: supplier.notes || "",
        });
      }
    }
  }, [context, selectedId, suppliers]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: e.target.name as keyof SupplierInitialState,
      value: e.target.value,
    });
    setTouch(true);
  };

  const handleFormVerification = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    const errors = {} as Record<
      keyof Omit<SupplierInitialState, "errors">,
      string
    >;

    const requiredFields: (keyof Omit<SupplierInitialState, "errors">)[] = [
      "name",
    ];

    requiredFields.forEach((field) => {
      if (!state[field]) {
        errors[field] = `${field} is required`;
      }
    });

    // Validate email format if provided
    if (state.email && state.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(state.email)) {
        errors.email = "Invalid email format";
      }
    }

    if (Object.keys(errors).length > 0) {
      dispatch({
        type: FORM_ACTIONS.SET_ERRORS,
        errors: errors as Record<keyof SupplierInitialState, string>,
      });
      return;
    }
    return true;
  };

  const handleCreateFromPurchaseOrderWindow = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    const validForm = handleFormVerification(e);

    if (!validForm) return;

    const {
      name,
      email,
      phone,
      supplier_type,
      external_id,
      status,
      address,
      city,
      state: stateValue,
      postal_code,
      country,
      contact_person,
      payment_terms,
      notes,
    } = state;

    try {
      // **Create a new supplier**
      const newSupplier = await SuppliersClient.createSupplier({
        name: name!,
        email: email?.trim() || null,
        phone: phone?.trim() || null,
        supplier_type: supplier_type || null,
        external_id: external_id?.trim() || null,
        status: status || "active",
        address: address?.trim() || null,
        city: city?.trim() || null,
        state: stateValue?.trim() || null,
        postal_code: postal_code?.trim() || null,
        country: country?.trim() || null,
        contact_person: contact_person?.trim() || null,
        payment_terms: payment_terms?.trim() || null,
        notes: notes?.trim() || null,
        organization_id: baseUser!.organization_id!,
      });

      onInfoShow("Supplier created successfully");
      getSuppliersByOrganizationId(baseUser!.organization_id!, false);
      // Call callback to auto-select new supplier in purchase order form
      if (onSupplierCreated) {
        onSupplierCreated(newSupplier.id);
      }
      handleClose();
      setTouch(false);
    } catch (error: unknown) {
      if (error instanceof Error && error.message) {
        const errorMessage = returnErrorMessage(
          Entity.CUSTOMERS, // Reuse Entity enum, or create SUPPLIERS if needed
          error.message
        );
        onErrorShow(errorMessage || "Failed to save supplier");
        return;
      }
      onErrorShow("Failed to save supplier");
    }
  };

  const handleSubmit = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    try {
      const validForm = handleFormVerification(e);

      if (!validForm) return;

      const {
        name,
        email,
        phone,
        supplier_type,
        external_id,
        status,
        address,
        city,
        state: stateValue,
        postal_code,
        country,
        contact_person,
        payment_terms,
        notes,
      } = state;

      if (context === DRAWER_CONTEXT.EDIT_SUPPLIER && selectedId) {
        // **Update existing supplier**
        await SuppliersClient.updateSupplier({
          id: selectedId,
          name: name!,
          email: email?.trim() || null,
          phone: phone?.trim() || null,
          supplier_type: supplier_type || null,
          external_id: external_id?.trim() || null,
          status: status || "active",
          address: address?.trim() || null,
          city: city?.trim() || null,
          state: stateValue?.trim() || null,
          postal_code: postal_code?.trim() || null,
          country: country?.trim() || null,
          contact_person: contact_person?.trim() || null,
          payment_terms: payment_terms?.trim() || null,
          notes: notes?.trim() || null,
          organization_id: baseUser!.organization_id!,
        });
        closeOnSubmit();
        onInfoShow("Supplier updated successfully");
        getSuppliersByOrganizationId(baseUser!.organization_id!, false);
      } else {
        // **Create a new supplier**
        const newSupplier = await SuppliersClient.createSupplier({
          name: name!,
          email: email?.trim() || null,
          phone: phone?.trim() || null,
          supplier_type: supplier_type || null,
          external_id: external_id?.trim() || null,
          status: status || "active",
          address: address?.trim() || null,
          city: city?.trim() || null,
          state: stateValue?.trim() || null,
          postal_code: postal_code?.trim() || null,
          country: country?.trim() || null,
          contact_person: contact_person?.trim() || null,
          payment_terms: payment_terms?.trim() || null,
          notes: notes?.trim() || null,
          organization_id: baseUser!.organization_id!,
        });
        closeOnSubmit();
        onInfoShow("Supplier created successfully");
        getSuppliersByOrganizationId(baseUser!.organization_id!, false);
        // Call callback if provided
        if (onSupplierCreated) {
          onSupplierCreated(newSupplier.id);
        }
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.message) {
        const errorMessage = returnErrorMessage(
          Entity.CUSTOMERS, // Reuse Entity enum, or create SUPPLIERS if needed
          error.message
        );
        onErrorShow(errorMessage || "Failed to save supplier");
        return;
      }
      onErrorShow("Failed to save supplier");
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: 1, overflow: "auto", pb: 2 }}>
        <Section>
          <FormInput
            name="name"
            label="Name"
            value={state.name}
            onChange={handleChange}
            errorMessage={state.errors?.name}
            required
          />
          <FormInput
            name="email"
            label="Email"
            value={state.email}
            onChange={handleChange}
            errorMessage={state.errors?.email}
            type="email"
          />
          <FormInput
            name="phone"
            label="Phone"
            value={state.phone}
            onChange={handleChange}
            errorMessage={state.errors?.phone}
          />
          <FormAutocomplete
            label="Supplier Type"
            value={state.supplier_type || ""}
            onChange={(value: string) => {
              dispatch({
                type: FORM_ACTIONS.SET_FIELD,
                field: "supplier_type",
                value: value || null,
              });
              setTouch(true);
            }}
            dropdownPlaceholder="Select supplier type..."
            errorMessage={state.errors?.supplier_type}
            options={supplierTypeOptions}
          />
          <FormAutocomplete
            label="Status"
            value={state.status || "active"}
            onChange={(value: string) => {
              dispatch({
                type: FORM_ACTIONS.SET_FIELD,
                field: "status",
                value: value as "active" | "inactive" | "suspended",
              });
              setTouch(true);
            }}
            dropdownPlaceholder="Select status..."
            errorMessage={state.errors?.status}
            options={statusOptions}
          />
          <FormInput
            name="external_id"
            label="External ID"
            value={state.external_id}
            onChange={handleChange}
            errorMessage={state.errors?.external_id}
            placeholder="Optional: ID from external system (POS, ERP, etc.)"
          />
        </Section>

        <Section>
          <FormInput
            name="contact_person"
            label="Contact Person"
            value={state.contact_person}
            onChange={handleChange}
            errorMessage={state.errors?.contact_person}
            placeholder="Primary contact name"
          />
          <FormInput
            name="address"
            label="Street Address"
            value={state.address}
            onChange={handleChange}
            errorMessage={state.errors?.address}
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <FormInput
                name="city"
                label="City"
                value={state.city}
                onChange={handleChange}
                errorMessage={state.errors?.city}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FormInput
                name="state"
                label="State/Province"
                value={state.state}
                onChange={handleChange}
                errorMessage={state.errors?.state}
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <FormInput
                name="postal_code"
                label="Postal Code"
                value={state.postal_code}
                onChange={handleChange}
                errorMessage={state.errors?.postal_code}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FormInput
                name="country"
                label="Country"
                value={state.country}
                onChange={handleChange}
                errorMessage={state.errors?.country}
              />
            </Box>
          </Box>
        </Section>

        <Section>
          <FormInput
            name="payment_terms"
            label="Payment Terms"
            value={state.payment_terms}
            onChange={handleChange}
            errorMessage={state.errors?.payment_terms}
            placeholder="e.g., Net 30, Net 60, COD"
          />
          <FormInput
            name="notes"
            label="Notes"
            value={state.notes}
            onChange={handleChange}
            errorMessage={state.errors?.notes}
            placeholder="Additional notes about this supplier"
          />
        </Section>
      </Box>

      <Footer
        handleClose={handleClose}
        handleSubmit={
          createFromPurchaseOrderView
            ? handleCreateFromPurchaseOrderWindow
            : handleSubmit
        }
      />
    </Box>
  );
};
